<template>
  <div>
    <page-header title="Corredoras" :breadcrumRoutes="routes">
      <template #buttons>
        <a-button v-if="canEdit" type="primary" @click="openCreate">
          Crear corredora
        </a-button>
      </template>
    </page-header>

    <a-table
      :data-source="dataFetch"
      :columns="columns"
      :scroll="{ x: 1200 }"
      :loading="tableLoading"
    >
      <template #custom_id="{ text }"># {{ text }}</template>
      <template #custom_rut="{ text }">
        {{ rutHelper.formatRut(text) }}
      </template>
      <template #action="{ record }">
        <span style="display: flex" v-if="canEdit">
          <a-tooltip color="#05045D" :title="`Editar`">
            <a-button
              type="link"
              @click="openUpdate(record)"
              class="edit-holding-text"
            >
              <edit-outlined class="blue-icon" />
            </a-button>
          </a-tooltip>
          <a-tooltip color="#05045D" :title="`Eliminar`">
            <a-button type="link" @click="openDelete(record)">
              <delete-outlined class="delete-holding-text" />
            </a-button>
          </a-tooltip>
        </span>
      </template>
    </a-table>
    <!-- Modal -->
    <a-modal
      v-model:visible="visible"
      :wrapClassName="'modal-xs'"
      :title="modal.title"
      @ok="closeModal"
      centered
      :closable="!loading"
      :maskClosable="!loading"
    >
      <a-form
        layout="vertical"
        name="form"
        ref="formRef"
        :model="formState"
        :rules="rules"
      >
        <!-- CREATE / UPDATE -->
        <div v-if="modal.type !== 'Delete'">
          <span class="text-gray-8 fw-regular">RUT Corredora</span>
          <a-form-item name="rut" has-feedback>
            <a-input
              class="mt-2"
              placeholder="Ingresa RUT Corredora"
              v-model:value="formState.rut"
              :maxlength="12"
              @keyup="formatRuts"
            />
          </a-form-item>
          <span class="text-gray-8 fw-regular">Nombre / Razón social</span>
          <a-form-item name="name">
            <a-input
              class="mt-2"
              placeholder="Ingresa nombre o razón social"
              v-model:value="formState.name"
              :maxlength="50"
            />
          </a-form-item>
          <span class="text-gray-8 fw-regular">Label</span>
          <a-form-item name="label">
            <a-input
              class="mt-2"
              placeholder="Ingresa label (identificador interno)"
              v-model:value="formState.label"
              :maxlength="50"
            />
          </a-form-item>
        </div>
        <!-- DELETE -->
        <div v-if="modal.type === 'Delete'">
          <p>
            Para eliminar a la corredora
            <strong>{{ formState.name }}</strong>
            escribe en el recuadro “eliminar” y presiona continuar.
          </p>
          <a-form-item ref="eliminar" name="eliminar">
            <a-input
              v-model:value="formState.eliminar"
              placeholder="eliminar"
              :maxlength="8"
            />
          </a-form-item>
        </div>
      </a-form>
      <template #footer>
        <a-button key="back" @click="closeModal" :disabled="loading">
          Cancelar
        </a-button>
        <a-button
          type="primary"
          v-if="modal.type === 'Create'"
          key="submit"
          @click="corredoraCreate"
          :loading="loading"
          :disabled="disableCreate()"
        >
          Crear
        </a-button>
        <a-button
          type="primary"
          v-if="modal.type === 'Update'"
          key="submit"
          @click="corredoraUpdate"
          :loading="loading"
          :disabled="disableUpdate()"
        >
          Editar
        </a-button>
        <a-button
          v-if="modal.type === 'Delete'"
          type="danger"
          key="submit"
          @click="corredoraDelete"
          :loading="loading"
          :disabled="disableDelete()"
        >
          Eliminar
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { reactive, ref } from 'vue';
import { computed } from 'vue';
import { useStore } from 'vuex';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import {
  FETCH_INSURANCE_BROKERS,
  GET_INSURANCE_BROKERS,
  POST_INSURANCE_BROKER,
  PUT_INSURANCE_BROKER,
  DELETE_INSURANCE_BROKER,
  GET_LOGGED_USER
} from '@/store/types';
import rutHelper from '@/utils/Ruts';
import openNotificationWithIcon from '@/utils/notifications';
import PageHeader from '@/components/page-header.vue';
import delay from '@/utils/delay';

const routes = [
  {
    path: '/corredor',
    breadcrumbName: 'Corredora'
  }
];

export default {
  name: 'CorredoraDashboard',
  components: {
    'page-header': PageHeader,
    'edit-outlined': EditOutlined,
    'delete-outlined': DeleteOutlined
  },
  setup() {
    const store = useStore();
    const tableLoading = ref(false);

    const getInsuranceBrokers = async () => {
      tableLoading.value = true;
      try {
        return await store.dispatch(FETCH_INSURANCE_BROKERS);
      } finally {
        tableLoading.value = false;
      }
    };
    getInsuranceBrokers();

    const dataFetch = computed(() => store.getters[GET_INSURANCE_BROKERS]);
    const role = store.getters[GET_LOGGED_USER].roleName;
    // create/update/delete are Role.ADMIN-only in the backend (tag
    // 'superadmin') - hide the actions entirely for anyone else instead of
    // showing a button that would just 401 on click.
    const canEdit = role === 'superadmin';

    // Table columns
    const columns = [
      {
        title: 'Id',
        dataIndex: 'id',
        key: 'id',
        slots: {
          customRender: 'custom_id'
        }
      },
      {
        title: 'RUT Corredora',
        dataIndex: 'rut',
        key: 'rut',
        slots: {
          customRender: 'custom_rut'
        }
      },
      {
        title: 'Razón social',
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label'
      },
      {
        title: 'Acción',
        dataIndex: 'id',
        key: 'action',
        slots: {
          customRender: 'action'
        }
      }
    ];

    // Modal
    const modal = reactive({ title: '', type: '' });
    const visible = ref(false);

    const showModal = () => {
      visible.value = true;
    };

    const closeModal = () => {
      visible.value = false;
    };

    const openCreate = () => {
      formState.id = null;
      formState.rut = '';
      formState.name = '';
      formState.label = '';

      modal.title = 'Crear corredora';
      modal.type = 'Create';

      showModal();
    };

    const openUpdate = (values) => {
      formState.id = null;
      formState.rut = '';
      formState.name = '';
      formState.label = '';

      formBackup.rut = values.rut;
      formBackup.name = values.name;
      formBackup.label = values.label;
      formState.id = values.id;
      formState.rut = rutHelper.formatRut(values.rut);
      formState.name = values.name;
      formState.label = values.label;
      modal.title = 'Editar corredora';
      modal.type = 'Update';

      showModal();
    };

    const openDelete = (values) => {
      formState.id = null;
      formState.rut = '';
      formState.name = '';
      formState.label = '';

      formState.name = values.name;
      formState.id = values.id;
      modal.title = 'Eliminar corredora';
      modal.type = 'Delete';

      showModal();
    };

    // Form
    const formRef = ref();
    const formState = reactive({
      id: null,
      rut: '',
      name: '',
      label: '',
      eliminar: ''
    });
    const formatRuts = () => {
      formState.rut = rutHelper.formatRut(formState.rut);
    };
    const formBackup = reactive({
      rut: '',
      name: '',
      label: ''
    });

    // Validations
    const loading = ref(false);

    const disableCreate = () => {
      let disabled = true;
      if (
        formState.rut.length >= 8 &&
        formState.name.length >= 3 &&
        formState.label.length >= 2
      ) {
        disabled = false;
      }
      return disabled;
    };

    const disableUpdate = () => {
      let disabled = true;
      if (
        formState.rut.length >= 8 &&
        formState.name.length >= 3 &&
        formState.label.length >= 2 &&
        (formState.name !== formBackup.name ||
          formState.rut !== formBackup.rut ||
          formState.label !== formBackup.label)
      ) {
        disabled = false;
      }
      return disabled;
    };

    const disableDelete = () => {
      let disabled = true;
      if (formState.eliminar === 'eliminar') {
        disabled = false;
      }
      return disabled;
    };

    const cleanForm = () => {
      formState.rut = '';
      formState.name = '';
      formState.label = '';
    };

    const checkRut = async (rule, value) => {
      let error_message = '';
      if (!value) {
        error_message = 'Ingrese RUT';
        return Promise.reject(error_message);
      }
      if (rutHelper.validateRut(value)) {
        return Promise.resolve();
      }
      error_message = 'RUT no válido';
      return Promise.reject(error_message);
    };

    const checkName = async (rule, value) => {
      let error_message = '';
      if (!value) {
        error_message = 'Ingrese un nombre o razón social';
        return Promise.reject(error_message);
      }
      if (value.length < 3) {
        const error_message = 'Largo mínimo de 3 caracteres';
        return Promise.reject(error_message);
      }
      return Promise.resolve();
    };

    const rules = {
      rut: [
        {
          required: true,
          type: 'string',
          validator: checkRut,
          trigger: 'change'
        }
      ],
      name: [
        {
          required: true,
          type: 'string',
          validator: checkName,
          trigger: 'change'
        }
      ]
    };

    const getErrorMessage = (error) => {
      let message = '';
      switch (error) {
        case 'El nombre para esta corredora ya existe':
          message = 'El nombre para esta corredora ya existe';
          break;
        case 'El Rut para esta corredora ya existe':
          message = 'El Rut para esta corredora ya existe';
          break;
        case 'El Rut y nombre para esta corredora ya existe':
          message = 'El Rut y nombre para esta corredora ya existe';
          break;
        case 'Ya existe una corredora con ese label':
          message = 'Ya existe una corredora con ese label';
          break;
        default:
          message = 'No se ha podido generar la operación, intente más tarde';
          break;
      }
      return message;
    };

    // Actions
    const corredoraCreate = async () => {
      loading.value = true;

      const payload = {
        rut: rutHelper.normalizeRut(formState.rut),
        businessName: formState.name,
        label: formState.label
      };

      try {
        await store.dispatch(POST_INSURANCE_BROKER, { payload });

        getInsuranceBrokers();
        openNotificationWithIcon('success', 'Correcto', 'Corredora Creada');
        await delay(1000);
      } catch (error) {
        const responseError = getErrorMessage(error.response?.data?.message);
        openNotificationWithIcon('error', 'Error', responseError);
      }

      loading.value = false;
      closeModal();
      cleanForm();
    };

    const corredoraUpdate = async () => {
      loading.value = true;
      const payload = {
        rut: rutHelper.normalizeRut(formState.rut),
        businessName: formState.name,
        label: formState.label
      };

      try {
        await store.dispatch(PUT_INSURANCE_BROKER, {
          insuranceBrokerId: formState.id,
          payload
        });

        getInsuranceBrokers();

        openNotificationWithIcon(
          'success',
          'Correcto',
          'Corredora Actualizada'
        );

        await delay(1000);
      } catch (error) {
        const responseError = getErrorMessage(error.response?.data?.message);
        openNotificationWithIcon('error', 'Error', responseError);
      }

      loading.value = false;
      cleanForm();
      closeModal();
    };

    const corredoraDelete = async () => {
      loading.value = true;
      try {
        await store.dispatch(DELETE_INSURANCE_BROKER, formState.id);

        openNotificationWithIcon(
          'success',
          'Correcto',
          'Corredora Eliminada'
        );
        getInsuranceBrokers();
      } catch (error) {
        const responseError = getErrorMessage(error.response?.data?.message);
        openNotificationWithIcon('error', 'Error', responseError);
      }
      loading.value = false;
      closeModal();
      cleanForm();
    };

    return {
      canEdit,
      // Variables
      routes,
      dataFetch,
      tableLoading,
      columns,
      visible,
      formRef,
      formState,
      loading,
      rules,
      modal,
      // Methods
      openCreate,
      openUpdate,
      openDelete,
      closeModal,
      disableCreate,
      disableUpdate,
      disableDelete,
      corredoraCreate,
      corredoraUpdate,
      corredoraDelete,
      formatRuts,
      rutHelper
    };
  }
};
</script>
